import { useEffect, useState } from "react";
import { DateInput } from "../components/DateInput";
import { Link } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../lib/api";
import { useOrg } from "../lib/session";
import { useWords } from "../lib/workspace";
import { money, toMajor, toMinor } from "../lib/plans";
import type { BookingResource, BookingService, BookingSetup, HourRow } from "../lib/types";
import { ErrorText, Field, Loading, PageHeader } from "../components/ui";
import { minutesLabel, zoned } from "../lib/tz";

const dayNames = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
const weekOrder = [6, 0, 1, 2, 3, 4, 5]; // Saturday first, as Gulf weeks are planned
const kindLabel: Record<BookingResource["kind"], string> = { person: "Person", room: "Room", equipment: "Equipment" };

const toMin = (t: string) => {
  const [h, m] = t.split(":").map(Number);
  return h * 60 + m;
};

export default function BookingSetupPage() {
  const { base, orgId } = useOrg();
  const qc = useQueryClient();
  const w = useWords();
  const key = [orgId, "booking-setup"];
  const setup = useQuery({ queryKey: key, queryFn: () => api<BookingSetup>(`${base}/bookings/setup`) });
  const refresh = () => qc.invalidateQueries({ queryKey: key });
  if (setup.isLoading) return <Loading />;
  if (setup.error) return <ErrorText error={setup.error} />;
  const s = setup.data!;
  return (
    <>
      <PageHeader title={`${w("service", true)} & hours`} actions={<Link className="button-link" to={`/o/${orgId}/calendar`}>Open calendar</Link>} />
      <p className="muted">What customers can book, who provides it and when you're open. Your AI agent books from the same calendar once you turn on "Book {w.lower("appointment", true)}" for it.</p>
      <Services setup={s} onChange={refresh} />
      <Resources setup={s} onChange={refresh} />
      <Hours setup={s} resourceId={null} onChange={refresh} />
      <TimeOff setup={s} onChange={refresh} />
      <Settings setup={s} onChange={refresh} />
    </>
  );
}

function Services({ setup, onChange }: { setup: BookingSetup; onChange: () => void }) {
  const [editing, setEditing] = useState<BookingService | "new" | null>(setup.services.length === 0 ? "new" : null);
  const w = useWords();
  return (
    <section className="card">
      <div className="inline" style={{ justifyContent: "space-between" }}>
        <h2 style={{ margin: 0 }}>{w("service", true)}</h2>
        {editing === null && <button className="secondary" onClick={() => setEditing("new")}>Add {w.lower("service")}</button>}
      </div>
      {setup.services.length === 0 && editing === null && <p className="muted small">No {w.lower("service", true)} yet.</p>}
      <ul className="list setup-list">
        {setup.services.map((svc) => (
          <li key={svc.id} className={svc.active ? "" : "inactive"}>
            <div>
              <b>{svc.name}</b> <span className="muted small">· {svc.duration_minutes} min{svc.buffer_minutes ? ` + ${svc.buffer_minutes} min gap` : ""}{svc.price_minor != null ? ` · ${money(svc.price_minor, svc.currency)}` : ""}</span>
              <div className="muted small">
                {svc.resource_ids.length === 0 ? "Anyone can provide it" : svc.resource_ids.map((id) => setup.resources.find((r) => r.id === id)?.name).filter(Boolean).join(", ")}
                {!svc.bookable_by_ai && " · staff book only"}
                {!svc.active && " · hidden"}
              </div>
            </div>
            <button className="link" onClick={() => setEditing(svc)}>Edit</button>
          </li>
        ))}
      </ul>
      {editing && <ServiceForm setup={setup} svc={editing === "new" ? null : editing} onDone={() => { setEditing(null); onChange(); }} onCancel={() => setEditing(null)} />}
    </section>
  );
}

function ServiceForm({ setup, svc, onDone, onCancel }: { setup: BookingSetup; svc: BookingService | null; onDone: () => void; onCancel: () => void }) {
  const { base } = useOrg();
  const w = useWords();
  const currency = svc?.currency ?? "BHD";
  const [name, setName] = useState(svc?.name ?? "");
  const [description, setDescription] = useState(svc?.description ?? "");
  const [duration, setDuration] = useState(String(svc?.duration_minutes ?? 30));
  const [buffer, setBuffer] = useState(String(svc?.buffer_minutes ?? 0));
  const [price, setPrice] = useState(svc?.price_minor != null ? toMajor(svc.price_minor, currency) : "");
  const [cur, setCur] = useState(currency);
  const [ai, setAi] = useState(svc?.bookable_by_ai ?? true);
  const [active, setActive] = useState(svc?.active ?? true);
  const [who, setWho] = useState<string[]>(svc?.resource_ids ?? []);
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  const people = setup.resources.filter((r) => r.active);
  async function save() {
    setBusy(true);
    setError(null);
    const minor = toMinor(price, cur);
    const body = {
      name, description, duration_minutes: Number(duration), buffer_minutes: Number(buffer) || 0, currency: cur, bookable_by_ai: ai, active, resource_ids: who,
      ...(minor == null ? (svc ? { clear_price: true } : {}) : { price_minor: minor }),
    };
    try {
      await api(svc ? `${base}/bookings/services/${svc.id}` : `${base}/bookings/services`, { method: svc ? "PATCH" : "POST", json: body });
      onDone();
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  }
  return (
    <div className="setup-form">
      <div className="row">
        <Field label={`${w("service")} name`}><input value={name} maxLength={120} placeholder="e.g. Teeth whitening" onChange={(e) => setName(e.target.value)} /></Field>
        <Field label="Length (minutes)"><input type="number" min={5} max={720} step={5} value={duration} onChange={(e) => setDuration(e.target.value)} /></Field>
      </div>
      <div className="row">
        <Field label="Price (optional)"><input inputMode="decimal" value={price} placeholder="85.000" onChange={(e) => setPrice(e.target.value)} /></Field>
        <Field label="Currency"><input value={cur} maxLength={3} onChange={(e) => setCur(e.target.value.toUpperCase())} /></Field>
        <Field label="Gap after (minutes)"><input type="number" min={0} max={240} step={5} value={buffer} onChange={(e) => setBuffer(e.target.value)} /></Field>
      </div>
      <Field label="Description for customers and the AI (optional)"><textarea rows={2} maxLength={1000} value={description} onChange={(e) => setDescription(e.target.value)} /></Field>
      {people.length > 0 && (
        <fieldset className="mini-form">
          <legend className="small">Who provides it (none ticked: anyone)</legend>
          <div className="check-grid">
            {people.map((r) => (
              <label key={r.id} className="check">
                <input type="checkbox" checked={who.includes(r.id)} onChange={(e) => setWho(e.target.checked ? [...who, r.id] : who.filter((x) => x !== r.id))} /> {r.name}
              </label>
            ))}
          </div>
        </fieldset>
      )}
      <label className="check"><input type="checkbox" checked={ai} onChange={(e) => setAi(e.target.checked)} /> The AI agent may book it in chat</label>
      <label className="check"><input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} /> Offered (untick to hide it without deleting)</label>
      <ErrorText error={error} />
      <div className="actions">
        <button disabled={busy || !name.trim()} onClick={save}>{busy ? "Saving…" : `Save ${w.lower("service")}`}</button>
        <button className="secondary" onClick={onCancel}>Cancel</button>
        {svc && (
          <button className="link danger-text" onClick={async () => {
            if (!confirm(`Delete "${svc.name}"? Past appointments keep its name.`)) return;
            try {
              await api(`${base}/bookings/services/${svc.id}`, { method: "DELETE" });
              onDone();
            } catch (err) {
              setError(err);
            }
          }}>Delete</button>
        )}
      </div>
    </div>
  );
}

function Resources({ setup, onChange }: { setup: BookingSetup; onChange: () => void }) {
  const { base } = useOrg();
  const w = useWords();
  const [name, setName] = useState("");
  const [kind, setKind] = useState<BookingResource["kind"]>("person");
  const [error, setError] = useState<unknown>(null);
  const [hoursFor, setHoursFor] = useState<string | null>(null);
  return (
    <section className="card">
      <h2>{w("resource", true)} &amp; rooms</h2>
      <p className="muted small">Each person, room or chair that can be booked. {w("appointment", true)} can't overlap on the same one.</p>
      <ul className="list setup-list">
        {setup.resources.map((r) => (
          <li key={r.id} className={r.active ? "" : "inactive"}>
            <div>
              <span className="dot" style={{ background: r.color }} /> <b>{r.name}</b> <span className="muted small">· {kindLabel[r.kind]} · {r.own_hours ? "own hours" : "business hours"}{!r.active && " · off"}</span>
            </div>
            <div className="inline">
              <button className="link small" onClick={() => setHoursFor(hoursFor === r.id ? null : r.id)}>Hours</button>
              <input type="color" aria-label={`${r.name} colour`} value={r.color} onChange={async (e) => { await api(`${base}/bookings/resources/${r.id}`, { method: "PATCH", json: { color: e.target.value } }); onChange(); }} />
              <button className="link small" onClick={async () => {
                setError(null);
                try {
                  await api(`${base}/bookings/resources/${r.id}`, { method: "PATCH", json: { active: !r.active } });
                  onChange();
                } catch (err) {
                  setError(err);
                }
              }}>{r.active ? "Turn off" : "Turn on"}</button>
              <button className="link small danger-text" onClick={async () => {
                if (!confirm(`Remove ${r.name}?`)) return;
                setError(null);
                try {
                  await api(`${base}/bookings/resources/${r.id}`, { method: "DELETE" });
                  onChange();
                } catch (err) {
                  setError(err);
                }
              }}>Remove</button>
            </div>
            {hoursFor === r.id && <div className="full"><Hours setup={setup} resourceId={r.id} onChange={onChange} /></div>}
          </li>
        ))}
      </ul>
      <form className="inline add-row" onSubmit={async (e) => {
        e.preventDefault();
        setError(null);
        try {
          await api(`${base}/bookings/resources`, { method: "POST", json: { name, kind } });
          setName("");
          onChange();
        } catch (err) {
          setError(err);
        }
      }}>
        <input value={name} maxLength={80} placeholder="e.g. Dr. Mariam, Room 2" onChange={(e) => setName(e.target.value)} aria-label="Name" />
        <select value={kind} onChange={(e) => setKind(e.target.value as BookingResource["kind"])} aria-label="Type">
          <option value="person">Person</option>
          <option value="room">Room</option>
          <option value="equipment">Equipment</option>
        </select>
        <button type="submit" disabled={!name.trim()}>Add</button>
      </form>
      <ErrorText error={error} />
    </section>
  );
}

/** Hours edits the weekly hours of the business (resourceId null) or one resource. */
function Hours({ setup, resourceId, onChange }: { setup: BookingSetup; resourceId: string | null; onChange: () => void }) {
  const { base } = useOrg();
  const mine = setup.hours.filter((h) => h.resource_id === resourceId);
  const usesBusiness = resourceId !== null && mine.length === 0;
  const source = usesBusiness ? setup.hours.filter((h) => h.resource_id === null) : mine;
  const toRows = (src: HourRow[]) => Object.fromEntries(weekOrder.map((wd) => [wd, src.filter((h) => h.weekday === wd).map((h) => [minutesLabel(h.start_minute), minutesLabel(h.end_minute)] as [string, string])]));
  const [rows, setRows] = useState<Record<number, [string, string][]>>(() => toRows(source));
  const [custom, setCustom] = useState(!usesBusiness);
  const [error, setError] = useState<unknown>(null);
  const [saved, setSaved] = useState(false);
  useEffect(() => setRows(toRows(source)), [setup]); // eslint-disable-line react-hooks/exhaustive-deps

  async function save(useBusiness = false) {
    setError(null);
    setSaved(false);
    const hours = useBusiness ? [] : weekOrder.flatMap((wd) => (rows[wd] ?? []).map(([a, b]) => ({ weekday: wd, start_minute: toMin(a), end_minute: b === "00:00" ? 1440 : toMin(b) })));
    try {
      await api(`${base}/bookings/hours`, { method: "PUT", json: { resource_id: resourceId, hours } });
      setSaved(true);
      onChange();
    } catch (err) {
      setError(err);
    }
  }
  const body = (
    <>
      <table className="hours-table">
        <tbody>
          {weekOrder.map((wd) => {
            const periods = rows[wd] ?? [];
            return (
              <tr key={wd}>
                <th>{dayNames[wd]}</th>
                <td>
                  {periods.length === 0 && <span className="muted small">Closed</span>}
                  {periods.map(([a, b], i) => (
                    <span key={i} className="period">
                      <input type="time" step={900} value={a} aria-label={`${dayNames[wd]} opens`} onChange={(e) => setRows({ ...rows, [wd]: periods.map((p, j) => (j === i ? [e.target.value, p[1]] : p)) })} />
                      –
                      <input type="time" step={900} value={b} aria-label={`${dayNames[wd]} closes`} onChange={(e) => setRows({ ...rows, [wd]: periods.map((p, j) => (j === i ? [p[0], e.target.value] : p)) })} />
                      <button type="button" className="link small" onClick={() => setRows({ ...rows, [wd]: periods.filter((_, j) => j !== i) })} aria-label="Remove period">✕</button>
                    </span>
                  ))}
                  <button type="button" className="link small" onClick={() => setRows({ ...rows, [wd]: [...periods, periods.length ? ["16:00", "20:00"] : ["09:00", "18:00"]] })}>
                    {periods.length ? "+ split shift" : "+ open"}
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <ErrorText error={error} />
      <div className="inline">
        <button onClick={() => save()}>Save hours</button>
        {saved && <span className="muted small">Saved.</span>}
      </div>
    </>
  );
  if (resourceId === null) {
    return (
      <section className="card">
        <h2>Opening hours</h2>
        <p className="muted small">When appointments can be booked, in {setup.settings.timezone}. Staff and rooms use these unless they have their own.</p>
        {body}
      </section>
    );
  }
  return (
    <div className="resource-hours">
      <label className="check"><input type="radio" checked={!custom} onChange={() => { setCustom(false); save(true); }} /> Same as the business's opening hours</label>
      <label className="check"><input type="radio" checked={custom} onChange={() => setCustom(true)} /> Their own hours</label>
      {custom && body}
    </div>
  );
}

function TimeOff({ setup, onChange }: { setup: BookingSetup; onChange: () => void }) {
  const { base } = useOrg();
  const tz = setup.settings.timezone;
  const [who, setWho] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [reason, setReason] = useState("");
  const [error, setError] = useState<unknown>(null);
  const [note, setNote] = useState<string | null>(null);
  const fmt = (iso: string) => new Date(iso).toLocaleString("en-GB", { timeZone: tz, dateStyle: "medium", timeStyle: "short" });
  return (
    <section className="card">
      <h2>Holidays &amp; time off</h2>
      <p className="muted small">Nothing can be booked during these, for the whole business or one person or room.</p>
      <ul className="list setup-list">
        {setup.time_off.map((o) => (
          <li key={o.id}>
            <div>
              <b>{o.reason ?? "Time off"}</b> <span className="muted small">· {o.resource_id ? setup.resources.find((r) => r.id === o.resource_id)?.name : "Whole business"} · {fmt(o.starts_at)} → {fmt(o.ends_at)}</span>
            </div>
            <button className="link small" onClick={async () => { await api(`${base}/bookings/time-off/${o.id}`, { method: "DELETE" }); onChange(); }}>Remove</button>
          </li>
        ))}
      </ul>
      <form className="setup-form" onSubmit={async (e) => {
        e.preventDefault();
        setError(null);
        setNote(null);
        const [y1, m1, d1] = fromDate.split("-").map(Number);
        const [y2, m2, d2] = (toDate || fromDate).split("-").map(Number);
        try {
          const r = await api<{ clashing_appointments: number }>(`${base}/bookings/time-off`, {
            method: "POST",
            json: { ...(who && { resource_id: who }), starts_at: zoned(y1, m1, d1, 0, 0, tz).toISOString(), ends_at: zoned(y2, m2, d2 + 1, 0, 0, tz).toISOString(), reason },
          });
          if (r.clashing_appointments > 0) setNote(`${r.clashing_appointments} appointment(s) are already booked in that time. Move or cancel them in the calendar.`);
          setFromDate("");
          setToDate("");
          setReason("");
          onChange();
        } catch (err) {
          setError(err);
        }
      }}>
        <div className="row">
          <Field label="Who">
            <select value={who} onChange={(e) => setWho(e.target.value)}>
              <option value="">Whole business</option>
              {setup.resources.filter((r) => r.active).map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
            </select>
          </Field>
          <Field label="From"><DateInput required value={fromDate} onChange={setFromDate} /></Field>
          <Field label="To (inclusive)"><DateInput value={toDate} min={fromDate} onChange={setToDate} /></Field>
        </div>
        <Field label="Reason"><input value={reason} maxLength={200} placeholder="e.g. Eid holiday" onChange={(e) => setReason(e.target.value)} /></Field>
        <ErrorText error={error} />
        {note && <p className="notice small">{note}</p>}
        <div><button type="submit" disabled={!fromDate}>Add time off</button></div>
      </form>
    </section>
  );
}

function Settings({ setup, onChange }: { setup: BookingSetup; onChange: () => void }) {
  const { base, orgId } = useOrg();
  const st = setup.settings;
  const [interval, setSlotInterval] = useState(String(st.slot_interval_minutes));
  const [notice, setNotice] = useState(String(st.min_notice_minutes / 60));
  const [ahead, setAhead] = useState(String(st.max_days_ahead));
  const [deals, setDeals] = useState(st.deal_on_booking);
  const [emails, setEmails] = useState(st.booking_emails ?? true);
  const [error, setError] = useState<unknown>(null);
  const [saved, setSaved] = useState(false);
  return (
    <section className="card">
      <h2>Booking rules</h2>
      <div className="row">
        <Field label="Start times every">
          <select value={interval} onChange={(e) => setSlotInterval(e.target.value)}>
            {[5, 10, 15, 20, 30, 60].map((n) => <option key={n} value={n}>{n} minutes</option>)}
          </select>
        </Field>
        <Field label="Earliest booking (hours ahead)"><input type="number" min={0} max={336} step={0.5} value={notice} onChange={(e) => setNotice(e.target.value)} /></Field>
        <Field label="Book up to (days ahead)"><input type="number" min={1} max={365} value={ahead} onChange={(e) => setAhead(e.target.value)} /></Field>
      </div>
      <p className="muted small">
        Reminders, confirmations and follow-ups are set up in <Link to={`/o/${orgId}/automations`}>Automations</Link>.
      </p>
      <label className="check"><input type="checkbox" checked={emails} onChange={(e) => setEmails(e.target.checked)} /> Email customers when they book, change or cancel (with buttons to change the time or cancel)</label>
      <label className="check"><input type="checkbox" checked={deals} onChange={(e) => setDeals(e.target.checked)} /> Open a deal for each booking (won when attended, lost on no-show or cancellation)</label>
      <ErrorText error={error} />
      <div className="inline">
        <button onClick={async () => {
          setError(null);
          setSaved(false);
          try {
            await api(`${base}/bookings/settings`, { method: "PUT", json: {
              slot_interval_minutes: Number(interval), min_notice_minutes: Math.round(Number(notice) * 60), max_days_ahead: Number(ahead),
              deal_on_booking: deals, booking_emails: emails,
            } });
            setSaved(true);
            onChange();
          } catch (err) {
            setError(err);
          }
        }}>Save</button>
        {saved && <span className="muted small">Saved.</span>}
      </div>
    </section>
  );
}
